import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { usePageState } from "@/hooks/usePageState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { pageStateQueryOptions, PageAccessError } from "@/lib/queries";
import { PageError, PageAccessErrorComponent } from "@/features/auth/components/PageError";
import { usePreflightDraftTenantSync } from "@/features/preflight/hooks/usePreflightDraftTenantSync";

/**
 * Pre Flight layout route: holds the page-state access guard (KTD7/KTD13) and
 * renders its children (Hub index + Report detail) through the outlet only once
 * access is confirmed. A denied page-state surfaces the shared 403 treatment.
 */
export const Route = createFileRoute("/_authenticated/pre-flight")({
  beforeLoad: () => assertPagePermission("pre-flight"),
  loader: ({ context }) =>
    context.queryClient
      .ensureQueryData(pageStateQueryOptions("pre-flight"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      }),
  component: PreFlightLayout,
  errorComponent: PageAccessErrorComponent,
});

function PreFlightLayout() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.preFlight", "Pre Flight"));
  usePreflightDraftTenantSync();
  const { isLoading, error } = usePageState("pre-flight");

  if (isLoading) {
    return <div className="flex h-full items-center justify-center">Loading…</div>;
  }

  if (error) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  return <Outlet />;
}
